import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { ReactNode } from "react";

import {
  ClientBudgetMonthContext,
  ClientBudgetMonthContextType,
} from "./ClientBudgetMonthContext";

import { campaignJobBoardQueryKeys } from "@/features/campaigns/jobboards/api/queryKeys";
import { invoiceQueryKeys } from "@/features/invoices/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { GetClientBudgetMonthResponse } from "@/types/response/GetClientBudgetMonthResponse";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

export interface UpdateMonthBudgetCapResponse {
  allocableBudget: number;
  allocatedBudget: number;
  budgetCap: number;
  clientId2: string;
  spent: number;
}

export interface UpdateMonthBudgetCapPayload {
  clientId: string;
  payload: {
    amount: number;
    month: string;
    year: string;
  };
}

interface ClientBudgetMonthProviderProps {
  children: ReactNode;
  clientId: string;
  month: string;
  year: string;
}

const updateBudgetCapApi = async ({
  clientId,
  payload: { year, month, amount },
}: UpdateMonthBudgetCapPayload): Promise<
  AxiosResponse<UpdateMonthBudgetCapResponse>
> => {
  return instance.put<UpdateMonthBudgetCapResponse>(
    `/api/v2/clients/${clientId}/budget/${year}/${month}/cap`,
    {
      amount,
    }
  );
};

export const ClientBudgetMonthProvider = ({
  children,
  clientId,
  year,
  month,
}: ClientBudgetMonthProviderProps) => {
  const queryClient = useQueryClient();
  const {
    data: clientBudgetMonth,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: campaignJobBoardQueryKeys.clientBudgetMonth.detail(clientId, year, month),
    queryFn: () =>
      instance
        .get<GetClientBudgetMonthResponse>(
          `/api/v2/clients/${clientId}/budget/${year}/${month}`
        )
        .then((res) => res.data),
    enabled: !!clientId && !!year && !!month,
    refetchOnMount: true,
    placeholderData: keepPreviousData,
    staleTime: 20 * 60 * 1000,
  });

  const updateBudgetCap = useMutation<
    AxiosResponse<UpdateMonthBudgetCapResponse>,
    AxiosError<ValidationErrorResponse>,
    UpdateMonthBudgetCapPayload["payload"],
    { previous?: GetClientBudgetMonthResponse }
  >({
    mutationFn: async ({
      amount,
      month,
      year,
    }: UpdateMonthBudgetCapPayload["payload"]) =>
      updateBudgetCapApi({ clientId, payload: { amount, month, year } }),
    onMutate: async ({ amount, month: mutMonth, year: mutYear }) => {
      const key = campaignJobBoardQueryKeys.clientBudgetMonth.detail(
        clientId,
        mutYear,
        mutMonth
      );

      await queryClient.cancelQueries({ queryKey: key });

      const previous =
        queryClient.getQueryData<GetClientBudgetMonthResponse>(key);

      if (previous) {
        queryClient.setQueryData<GetClientBudgetMonthResponse>(key, {
          ...previous,
          budgetCap: amount,
        });
      }

      return { previous };
    },
    onError: (_, { month: mutMonth, year: mutYear }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          campaignJobBoardQueryKeys.clientBudgetMonth.detail(
            clientId,
            mutYear,
            mutMonth
          ),
          context.previous
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: campaignJobBoardQueryKeys.clientBudgetMonth.byClient(clientId),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.transactions.byClient(clientId),
        exact: false,
      });
    },
  });

  const value: ClientBudgetMonthContextType = {
    clientBudgetMonth,
    isFetching,
    isLoading,
    updateBudgetCap,
  };

  return (
    <ClientBudgetMonthContext.Provider value={value}>
      {children}
    </ClientBudgetMonthContext.Provider>
  );
};
